import { PersonStatusCodeType, PersonUserFlagType } from 'constants/person';

//
// --- Types ---

export type SupplierIdKeyType = keyof typeof SUPPLIER_ID;
export type SupplierIdType = typeof SUPPLIER_ID[SupplierIdKeyType];

export type SupplierTeamEntityNameKeyType = keyof typeof SUPPLIER_TEAM_ENTITY_NAME;
export type SupplierTeamEntityNameType = typeof SUPPLIER_TEAM_ENTITY_NAME[SupplierTeamEntityNameKeyType];

// --- Supplier Vendor Mapping Types ---

interface SupplierVendorMappingSupplierLocation {
  address: string;
  addressKey: string;
  city: string;
  country: string;
  enhanced: string;
  latitude: string | null;
  locationId: string;
  longitude: string | null;
  siteName: string;
  siteTypeName: string;
  stateProvince: string;
  zipCode: string;
}

export interface SupplierVendorMappingSupplier {
  diverse: boolean;
  diversity_qualifications: ReadonlyArray<string>;
  id: string;
  name: string;
  supplier_locations: ReadonlyArray<SupplierVendorMappingSupplierLocation>;
  supplier_vendor: { name: 'supplier' };
}

interface SupplierVendorMappingVendorEIPPerson {
  businessTitle: string;
  cellPhone: string;
  companyKey: number;
  emailAddress: string;
  firstName: string;
  lastLoginSuccess: number; // timestamp
  lastName: string;
  personKey: number;
  personStatusCode: PersonStatusCodeType;
  userFlag: PersonUserFlagType;
  username: string;
  workPhone: string;
}

interface SupplierVendorMappingVendorEIPAddress {
  addressKey: string;
  addressStatusCode: string;
  addressTypeCode: string;
  city: string;
  companyKey: string;
  country: string;
  line1: string;
  line2: string;
  phone: string;
  postalCode: string;
  siteDescription: string;
  siteName: string;
  siteTypeCode: string;
  stateProvince: string;
}

interface SupplierVendorMappingVendorEIPCompanyMetaData {
  bidEventsInvited: number;
  buyersRegisteredToKeys: ReadonlyArray<number>;
  companyKey: string;
  companyName: string;
  companyStatusCode: string;
  diversityClassifications: ReadonlyArray<number>;
  itemKeys: ReadonlyArray<number>;
  supplierItems: ReadonlyArray<number>;
  supplierQualifications: ReadonlyArray<number>;
  supplierSegmentAssignmentKeys: ReadonlyArray<number>;
  website: string;
}

export interface SupplierVendorMappingVendor {
  eip_address: ReadonlyArray<SupplierVendorMappingVendorEIPAddress>;
  eip_company_metadata: SupplierVendorMappingVendorEIPCompanyMetaData;
  eip_companykey: number;
  eip_person: ReadonlyArray<SupplierVendorMappingVendorEIPPerson>;
  name: string;
  supplier_id: string;
  supplier_vendor: { name: 'vendor'; parent: string };
}

export interface SupplierVendorMappingItem {
  supplier: SupplierVendorMappingSupplier;
  vendors: ReadonlyArray<SupplierVendorMappingVendor>;
}

export type SupplierVendorMappingCollection = ReadonlyArray<SupplierVendorMappingItem>;

//
// --- Constants ---

export const SUPPLIER_ID = {
  EIP_COMPANY_PREFIX: 'EIP_',
  FAMILIED_PREFIX: 'S_',
  UNFAMILIED: 'S_0',
  UNFAMILIED_VENDOR_PREFIX: 'UV_'
} as const;

/**
 * "Entity Name" is a concept internal to this app. Its a string of text that describes the
 * type of supplier team being used, where "supplier team" is a generic term used for
 * bid teams, supplier groups, etc
 */
export const SUPPLIER_TEAM_ENTITY_NAME = {
  BID_TEAM: 'Bid Team',
  SUPPLIER_GROUP: 'Supplier Group'
} as const;
